import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import ReactGA from 'react-ga4';
import './ReSEOPage.css';

const ReSEOPage = () => {
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [seoData, setSeoData] = useState(null);
  const [copiedSection, setCopiedSection] = useState(null);

  const handleAnalyze = async () => {
    if (!youtubeUrl.trim()) {
      setError('Please enter a YouTube URL');
      return;
    }

    setLoading(true);
    setError(null);
    setSeoData(null);

    try {
      // Track event
      ReactGA.event({
        category: 'Re-SEO',
        action: 'Analyze SEO',
        label: youtubeUrl
      });

      const response = await apiService.getReSEO(youtubeUrl);

      if (response.success && response.data) {
        setSeoData(response.data);
      } else {
        setError(response.error || 'Failed to analyze SEO');
      }
    } catch (err) {
      console.error('SEO analysis error:', err);
      setError(err.response?.data?.error || 'Failed to analyze SEO. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text, section) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedSection(section);
      setTimeout(() => setCopiedSection(null), 2000);

      ReactGA.event({
        category: 'Re-SEO',
        action: 'Copy',
        label: section
      });
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const parseTranscript = (text) => {
    if (!text) return [];
    const lines = text.split('\n');
    const timeline = [];
    const timestampRegex = /\[(\d{1,2}:\d{2}(?::\d{2})?)\]\s*(.*)/;

    lines.forEach(line => {
      const match = line.match(timestampRegex);
      if (match) {
        timeline.push({
          timestamp: match[1],
          content: match[2].trim()
        });
      } else if (line.trim() && timeline.length > 0) {
        // Append to previous item if no timestamp but has text
        timeline[timeline.length - 1].content += ' ' + line.trim();
      }
    });
    return timeline;
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleAnalyze();
    }
  };

  return (
    <div className="reseo-page">
      <div className="reseo-container">
        <div className="reseo-header">
          <h1 className="reseo-title">Re-SEO Analysis</h1>
          <p className="reseo-subtitle">
            Get optimized title and description recommendations with timestamped transcript
          </p>
        </div>

        <div className="reseo-input-section">
          <div className="input-group">
            <input
              type="text"
              className="reseo-input"
              placeholder="Enter YouTube URL (e.g., https://www.youtube.com/watch?v=...)"
              value={youtubeUrl}
              onChange={(e) => setYoutubeUrl(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={loading}
            />
            <button
              className="reseo-analyze-btn"
              onClick={handleAnalyze}
              disabled={loading || !youtubeUrl.trim()}
            >
              {loading ? 'Analyzing...' : 'Analyze SEO'}
            </button>
          </div>
        </div>

        {error && (
          <div className="reseo-error">
            <span className="error-icon">⚠️</span>
            <span>{error}</span>
          </div>
        )}

        {loading && (
          <div className="reseo-loading">
            <div className="loading-spinner"></div>
            <p>Analyzing video and generating SEO recommendations...</p>
          </div>
        )}

        {seoData && (
          <div className="reseo-results">
            {/* Current Video Info */}
            <div className="video-info-card">
              <h3>Video Information</h3>
              <div className="video-info-content">
                <p><strong>Current Title:</strong> {seoData.current_title || 'N/A'}</p>
                <p><strong>Channel:</strong> {seoData.channel_name || 'N/A'}</p>
                <p><strong>Duration:</strong> {seoData.duration ? `${Math.floor(seoData.duration / 60)}:${String(Math.floor(seoData.duration % 60)).padStart(2, '0')}` : 'N/A'}</p>
              </div>
            </div>

            {/* SEO Recommendations */}
            <div className="seo-recommendations">
              {/* Recommended Title */}
              <div className="recommendation-card">
                <div className="recommendation-header">
                  <h3>📝 Recommended Title</h3>
                  <button
                    className="copy-btn"
                    onClick={() => copyToClipboard(seoData.recommended_title, 'title')}
                    title="Copy to clipboard"
                  >
                    {copiedSection === 'title' ? '✅ Copied!' : '📋 Copy'}
                  </button>
                </div>
                <div className="recommendation-content">
                  <p className="recommended-text">{seoData.recommended_title}</p>
                  {seoData.title_reason && (
                    <p className="recommendation-reason">{seoData.title_reason}</p>
                  )}
                </div>
              </div>

              {/* Recommended Description */}
              <div className="recommendation-card">
                <div className="recommendation-header">
                  <h3>📄 Recommended Description</h3>
                  <button
                    className="copy-btn"
                    onClick={() => copyToClipboard(seoData.recommended_description, 'description')}
                    title="Copy to clipboard"
                  >
                    {copiedSection === 'description' ? '✅ Copied!' : '📋 Copy'}
                  </button>
                </div>
                <div className="recommendation-content">
                  <div className="recommended-description">
                    {seoData.recommended_description.split('\n').map((line, idx) => (
                      <p key={idx}>{line}</p>
                    ))}
                  </div>
                  {seoData.description_reason && (
                    <p className="recommendation-reason">{seoData.description_reason}</p>
                  )}
                </div>
              </div>

              {/* Current Description for Comparison */}
              {seoData.current_description && (
                <div className="comparison-card">
                  <h3>📋 Current Description</h3>
                  <div className="current-description">
                    {seoData.current_description.split('\n').map((line, idx) => (
                      <p key={idx}>{line}</p>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Timestamped Transcript Timeline */}
            {seoData.transcript_with_timestamps && (
              <div className="transcript-section">
                <div className="transcript-header">
                  <h3>📜 Video Timeline</h3>
                  <button
                    className="copy-btn"
                    onClick={() => copyToClipboard(seoData.transcript_with_timestamps, 'transcript')}
                    title="Copy full transcript"
                  >
                    {copiedSection === 'transcript' ? '✅ Copied!' : '📋 Copy All'}
                  </button>
                </div>
                <div className="timeline-container">
                  {parseTranscript(seoData.transcript_with_timestamps).map((item, index) => (
                    <div key={index} className="timeline-item">
                      <div className="timeline-timestamp">
                        {item.timestamp}
                      </div>
                      <div className="timeline-content">
                        {item.content}
                      </div>
                    </div>
                  ))}
                  {parseTranscript(seoData.transcript_with_timestamps).length === 0 && (
                    <pre className="transcript-text">{seoData.transcript_with_timestamps}</pre>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReSEOPage;

